import { useEffect, useRef, useState } from 'react';

import styles from '@components/aktivitet-liste/styles/mine-verktoy-list.module.scss';
import AssuranceElement from '@components/container/assurance-element';
import { VerktoyLogo } from '@components/entry-page/verktoy-logo';
import FavoritterWrapper from '@components/shared/favoritter/favoritter-wrapper';
import VerktoyTagsComponent from '@components/tags-component/tags-component';
import VerktoyButtons from '@components/verktoy-details/components/buttons/verktoy-buttons';
import VerktoyMetaInfo from '@components/verktoy-details/components/metadata/verktoy-metainfo';
import { useGetResources } from '@hooks/useGetResources';
import { FavoritterContext } from '@src/types/FavoritterContext';
import { StatusType, type Verktoy, VerktoyFormal } from '@src/types/minhelseentities';
import { PageRoutes, SessionStorageKeys, TagGroup } from '@src/types/types';
import { useAppDispatch, useAppSelector } from '@store';
import { fetchMineVerktoyV2Thunk, fetchVerktoyListeThunk } from '@store/verktoy/thunks';
import { hentSokParam } from '@utils/queryParamUtil';
import { erLaeringsaktivitetMineVerktoyFn, getCurrentPageRoute, gyldigFraOgTilDato, scrollAndFocus } from '@utils/utils';
import { useLocation, useNavigate } from 'react-router';

import type { VerktoyFilterState } from '@src/types/stateTypes';

import Icon from '@helsenorge/designsystem-react/components/Icon';
import Toolbox from '@helsenorge/designsystem-react/components/Icons/Toolbox';
import Panel, { PanelVariant } from '@helsenorge/designsystem-react/components/Panel';
import PanelList from '@helsenorge/designsystem-react/components/PanelList';
import Title from '@helsenorge/designsystem-react/components/Title';
import { palette } from '@helsenorge/designsystem-react/theme/palette';

import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';

import finnVerktoyStyles from './styles/finn-verktoy.module.scss';
import VerktoyFilter from './verktoy-filter/verktoy-filter';

import '../../styles/verktoy-list.scss';

export const VerktoyList = (): React.JSX.Element | null => {
  const [isFirstUpdate, setIsFirstUpdate] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const resources = useGetResources();
  const mineVerktoy = useAppSelector(globalState => globalState.verktoy.mineVerktoyListe);
  const verktoyListeLoading = useAppSelector(globalState => globalState.verktoy.verktoyListeLoading);
  const verktoyFilters = useAppSelector(globalState => globalState.verktoy.verktoyFilters);
  const verktoyListe = useAppSelector(globalState => globalState.verktoy.verktoyListe);

  const verktoyRef = useRef<HTMLLIElement>(null);
  const returnLocationFromSessionStorage = sessionStorage.getItem(SessionStorageKeys.returnLocation);

  const verktoySomSkalVises = verktoyListe.filter((item: Verktoy) => {
    const shouldShow =
      item.StatusType === StatusType.Publisert &&
      item.VerktoyFormal !== VerktoyFormal.Informasjon &&
      gyldigFraOgTilDato(item.GyldigFraDato, item.GyldigTilDato);
    return shouldShow;
  });

  const handleFilterChange = (verktoyFilters: VerktoyFilterState): void => {
    dispatch(fetchVerktoyListeThunk({ isLoggedIn: isAuthorized(), verktoyFilters }));
  };

  const handleFetch = (): void => {
    if (!location.search) {
      dispatch(fetchVerktoyListeThunk({ isLoggedIn: isAuthorized(), verktoyFilters }));
    }
    if (isAuthorized() && !mineVerktoy.length) {
      dispatch(fetchMineVerktoyV2Thunk());
    }
  };

  const handleScrollAndFocus = (): void => {
    if (returnLocationFromSessionStorage) {
      sessionStorage.removeItem(SessionStorageKeys.returnLocation);
      navigate(returnLocationFromSessionStorage);
    } else if (!verktoyListeLoading && verktoyListe.length > 0 && isFirstUpdate) {
      scrollAndFocus(verktoyListe, verktoyRef);
      setIsFirstUpdate(false);
    }
  };

  const moreInfoButtonClicked = (verktoy: Verktoy): void => {
    sessionStorage.setItem(
      SessionStorageKeys.returnLocation,
      PageRoutes.finnVerktoy + '/' + verktoy.VerktoyId + '?' + hentSokParam(resources)
    );
    const url = getCurrentPageRoute(location.pathname);
    navigate(`/${verktoy.VerktoyId}`, { state: { from: url } });
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsFirstUpdate(true);
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handleScrollAndFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verktoyListeLoading, verktoyListe]);
  if (!resources) {
    return null;
  }
  return (
    <>
      <div className={finnVerktoyStyles.finnVerktoyTitleWithFavoritt}>
        <Title appearance="title1" htmlMarkup="h1">
          {resources.finnVerktoy_header}
        </Title>
        <FavoritterWrapper context={FavoritterContext.FinnVerktoy} />
      </div>
      <VerktoyFilter antallVerktoy={verktoySomSkalVises.length} changeFilterFn={handleFilterChange} isLoading={verktoyListeLoading} />
      <AssuranceElement extraClass={styles.assuranceMargin} />

      {!verktoyListeLoading && verktoySomSkalVises.length === 0 && (
        <div className="verktoy-list--empty">
          <p>{resources.alleVerktoyIsEmpty}</p>
          <Icon svgIcon={Toolbox} color={palette.neutral200} />
        </div>
      )}

      {!verktoyListeLoading && verktoySomSkalVises.length > 0 && (
        <div className="finn-verktoy-list">
          <PanelList variant={PanelVariant.fill}>
            {verktoySomSkalVises.map((verktoy: Verktoy) => (
              <Panel key={verktoy.VerktoyId} color="neutral">
                <Panel.PreContainer>
                  <VerktoyLogo verktoy={verktoy}></VerktoyLogo>
                </Panel.PreContainer>
                <Panel.Title title={verktoy.Navn} badge></Panel.Title>
                <Panel.A>
                  <VerktoyTagsComponent verktoy={verktoy} tagGroup={TagGroup.topTags} excludeLaeringFormaal={true} />
                </Panel.A>
                <Panel.B>
                  <div lang="nb-NO">{verktoy.Ingress}</div>
                </Panel.B>
                <VerktoyMetaInfo opprettetAv={verktoy.OpprettetDato}></VerktoyMetaInfo>

                <Panel.C>
                  <VerktoyButtons
                    verktoy={verktoy}
                    erLaeringsaktivitet={erLaeringsaktivitetMineVerktoyFn(mineVerktoy, verktoy.VerktoyId)}
                    resources={resources}
                    moreInfoButtonClicked={() => moreInfoButtonClicked(verktoy)}
                  />
                </Panel.C>
              </Panel>
            ))}
          </PanelList>
        </div>
      )}
    </>
  );
};

export default VerktoyList;
